import {
  Result,
  SelectList,
} from "api/baseApiResponseModel/BaseApiResponseModel";
import { IGeneralRepository } from "api/repositories/general/GeneralRepository";
import { TransactionDetailReportItem } from "api/repositories/general/model/TransactionDetailReportResponseModel";
import { TransactionReportItem } from "api/repositories/general/model/TransactionReportResponseModel";
import { defaultStoreRepository } from "api/repositories/store/StoreRepository";
import { defaultUserManagementRepository } from "api/repositories/userManagement/UserManagementRepository";
import { AuthenticationContext } from "context/AuthenticationContext";
import { isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { DataFormat } from "utils/format/DataFormat";
import { getMessage, paramsChecking } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

const GeneralViewModel = (repository: IGeneralRepository) => {
  const [loading, setLoading] = useState(false);
  const [dataForCharts, setDataForCharts] = useState([]);
  const [revenue, setRevenue] = useState({});
  const [resultObject, setResultObject] = useState<Result | null>(null);
  const [transactionReportData, setTransactionReportData] = useState<
    TransactionReportItem[]
  >([]);
  const [transactionReportLoading, setTransactionReportLoading] =
    useState(false);
  const [transactionDetailReportData, setTransactionDetailReportData] =
    useState<TransactionDetailReportItem[]>([]);
  const [transactionDetailReportLoading, setTransactionDetailReportLoading] =
    useState(false);

  // Store and staff state management
  const [storeOptions, setStoreOptions] = useState<Array<SelectList>>([
    { label: strings.GlobalLabels.All, value: "" },
  ]);
  const [staffOptions, setStaffOptions] = useState<
    { label: string; value: string; type?: number }[]
  >([{ label: strings.GlobalLabels.All, value: "" }]);
  const [selectedStore, setSelectedStore] = useState<string>("");
  const [selectedStaff, setSelectedStaff] = useState<string>("");

  const { config, user } = AuthenticationContext();

  const getDataForReports = async () => {
    try {
      setLoading(true);
      const res = await repository.getGeneralData();
      if (res?.data) {
        let temp = res?.data?.map((item) => {
          const time = moment(item?.time, "MM-YYYY").format("MM-YYYY");
          const data = {
            time: time,
            value: item?.total,
            count: item?.count,
          };
          return data;
        });
        setDataForCharts(temp);
        // setRevenue(res?.report || {});
      } else {
        setResultObject({
          type: "error",
          message: res?.message || strings.GlobalMessage.Error,
        });
      }
    } catch (error) {
      console.error(error);
      setResultObject({ type: "error", message: strings.GlobalMessage.Error });
    } finally {
      setLoading(false);
    }
  };

  const getTransactionReport = async (serviceCode: string) => {
    try {
      let data: any = serviceCode === "ALL" ? {} : { service: serviceCode };
      data.store = paramsChecking(selectedStore, "select");
      data.staff =
        selectedStaff !== selectedStore
          ? paramsChecking(selectedStaff, "select")
          : undefined;
      data.isOnlyStore =
        isEmpty(selectedStaff) || selectedStaff !== selectedStore
          ? undefined
          : "true";
      setTransactionReportLoading(true);
      const res = await repository.getTransactionReport(
        DataFormat.removeUndefined(data)
      );
      if (res?.data) {
        setTransactionReportData(res.data);
      } else {
        setTransactionReportData([]);
        setResultObject({
          type: "error",
          message: res?.message || strings.GlobalMessage.Error,
        });
      }
    } catch (error) {
      console.error(error);
      getMessage(error?.message, 3, "error");
    } finally {
      setTransactionReportLoading(false);
    }
  };

  const getTransactionDetailReport = async (
    serviceCode: string,
    reportType: number
  ) => {
    try {
      if (serviceCode !== "ALL") {
        setTransactionDetailReportLoading(true);
        let data: any = serviceCode === "ALL" ? {} : { service: serviceCode };
        data.type = reportType;
        data.store = paramsChecking(selectedStore, "select");
        data.staff =
          selectedStaff !== selectedStore
            ? paramsChecking(selectedStaff, "select")
            : undefined;
        data.isOnlyStore =
          isEmpty(selectedStaff) || selectedStaff !== selectedStore
            ? undefined
            : "true";
        const res = await repository.getTransactionDetailReport(
          DataFormat.removeUndefined(data)
        );
        if (res?.data) {
          setTransactionDetailReportData(res.data);
        } else {
          setTransactionDetailReportData([]);
          setResultObject({
            type: "error",
            message: res?.message || strings.GlobalMessage.Error,
          });
        }
      }
    } catch (error) {
      console.error(error);
      getMessage(error?.message, 3, "error");
    } finally {
      setTransactionDetailReportLoading(false);
    }
  };

  const exportTransactionDetailReport = async (
    serviceCode: string,
    reportType: number,
    extras?: { store?: string; staff?: string }
  ) => {
    const data: any = serviceCode === "ALL" ? {} : { service: serviceCode };
    data.type = reportType;
    if (extras?.store) data.store = extras.store;
    if (extras?.staff) data.staff = extras.staff;
    data.isOnlyStore =
      isEmpty(selectedStaff) || selectedStaff !== selectedStore
        ? undefined
        : "true";
    const res = await repository.exportTransactionDetailReport(
      DataFormat.removeUndefined(data)
    );
    return res?.data as unknown as Blob;
  };

  const loadStores = async () => {
    try {
      if (user.type === 3) {
        const res = await defaultStoreRepository.getStoreList({
          page: 0,
          limit: 500,
        } as any);
        const items = res?.data?.data?.items || [];
        const opts = [
          { label: strings.GlobalLabels.All, value: "" },
          ...items.map((s) => ({
            label: s?.name,
            value: s?.userId,
            code: s?.code,
          })),
        ];
        setStoreOptions(opts);
      }
    } catch (e) {
      setStoreOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    }
  };

  const loadStaff = async () => {
    try {
      // Do not load staff when no store is selected
      if (!selectedStore || isEmpty(selectedStore)) {
        setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);
        setSelectedStaff("");
        return;
      }
      if (user.type === 3 || user.type === 4) {
        const res = await defaultUserManagementRepository.getUserListV2({
          page: 0,
          limit: 500,
          store: storeOptions?.find((ele) => ele?.value === selectedStore)
            ?.code,
        } as any);
        const items = res?.data?.items || [];
        const opts = [
          { label: strings.GlobalLabels.All, value: "" },
          ...items.map((u) => ({
            label: u?.fullName || u?.userName || u?.phone,
            value: u?.id,
            type: u?.type,
          })),
        ];
        setStaffOptions(opts);
      }
    } catch (e) {
      setStaffOptions([{ label: strings.GlobalLabels.All, value: "" }]);
    }
  };

  // Load stores on component mount
  useEffect(() => {
    loadStores();
  }, [strings]);

  // Load staff when selectedStore changes
  useEffect(() => {
    loadStaff();
  }, [selectedStore, strings]);

  return {
    loading,
    dataForCharts,
    revenue,
    resultObject,
    config,
    transactionReportData,
    transactionReportLoading,
    getTransactionReport,
    transactionDetailReportData,
    transactionDetailReportLoading,
    getTransactionDetailReport,
    exportTransactionDetailReport,
    // Store and staff related
    storeOptions,
    staffOptions,
    selectedStore,
    selectedStaff,
    setSelectedStore,
    setSelectedStaff,
    loadStores,
    loadStaff,
  };
};

export default GeneralViewModel;
